/**
 * Passwordless OTP sign-in. Two steps:
 *   1. enter email        → POST /auth/otp/initiate → auth_request_id
 *   2. enter 6-digit code  → POST /auth/otp/verify   → session cookie + user
 *
 * Fully token-themed: .card / .btn-primary / .form-input / .form-label and
 * var(--…) tokens. No hardcoded colors or fonts — light / dark / high-contrast
 * all inherit from globals.css.
 */

import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { ShieldCheck, ArrowLeft } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';

type Step = 'email' | 'code';

const ERROR_COPY: Record<string, string> = {
  invalid_email: 'That email doesn’t look right.',
  email_not_allowed: 'This email isn’t permitted to sign in.',
  rate_limited: 'Too many requests. Wait a few minutes and try again.',
  invalid_code: 'That code is incorrect.',
  code_expired: 'That code has expired. Request a new one.',
  code_not_found: 'We couldn’t find that request. Start over.',
  too_many_attempts: 'Too many attempts. Request a new code.',
  otp_failed: 'Something went wrong sending your code. Try again.',
};

function friendlyError(e: unknown): string {
  if (e instanceof ApiError && ERROR_COPY[e.code]) return ERROR_COPY[e.code];
  return 'Something went wrong. Please try again.';
}

export function LoginPage() {
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [authRequestId, setAuthRequestId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleEmail(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await api.otpInitiate(email.trim().toLowerCase());
      setAuthRequestId(res.auth_request_id);
      setStep('code');
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleCode(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await api.otpVerify(authRequestId, code.trim());
      setUser(res.user);
      navigate('/', { replace: true });
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: '1rem',
        background: 'var(--bg-primary)',
      }}
    >
      <div className="card" style={{ width: '100%', maxWidth: 400 }}>
        <div style={{ textAlign: 'center', fontSize: 24 }}>
          <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>Sylvan </span>
          <span style={{ color: 'var(--accent-primary)', fontWeight: 600 }}>Authority</span>
        </div>
        <p
          style={{
            textAlign: 'center',
            color: 'var(--text-tertiary)',
            fontSize: 13,
            margin: '0.5rem 0 1.5rem',
          }}
        >
          A clean room for recording decisions.
        </p>

        {error && (
          <div
            className="badge-error"
            style={{
              display: 'block',
              padding: '0.6rem 0.75rem',
              borderRadius: 'var(--radius-md)',
              fontSize: 13,
              marginBottom: '1rem',
            }}
          >
            {error}
          </div>
        )}

        {step === 'email' ? (
          <form onSubmit={handleEmail} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div>
              <label className="form-label">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                autoFocus
                disabled={loading}
                className="form-input"
              />
            </div>
            <button type="submit" disabled={loading || !email} className="btn btn-primary" style={{ justifyContent: 'center' }}>
              {loading ? 'Sending…' : 'Send sign-in code'}
            </button>
            <p style={{ fontSize: 12, color: 'var(--text-tertiary)', textAlign: 'center', margin: 0 }}>
              <ShieldCheck size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} />
              No password. We email you a 6-digit code.
            </p>
          </form>
        ) : (
          <form onSubmit={handleCode} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div>
              <label className="form-label">Enter the 6-digit code sent to {email}</label>
              <input
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                required
                autoFocus
                disabled={loading}
                className="form-input"
                style={{ letterSpacing: '0.5em', textAlign: 'center', fontSize: 20 }}
              />
            </div>
            <button
              type="submit"
              disabled={loading || code.length !== 6}
              className="btn btn-primary"
              style={{ justifyContent: 'center' }}
            >
              {loading ? 'Verifying…' : 'Sign in'}
            </button>
            <button
              type="button"
              onClick={() => {
                setStep('email');
                setCode('');
                setError(null);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-tertiary)',
                fontSize: 13,
                cursor: 'pointer',
                padding: '0.25rem',
              }}
            >
              <ArrowLeft size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} />
              Use a different email
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
